<template>
  <div class="space-y-6 lg:space-y-6">
    <!-- Header -->
    <div>
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div class="flex-1">
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">{{ $t('BUSINESS_DATA.FAQS.TITLE') }}</h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">{{ $t('BUSINESS_DATA.FAQS.DESCRIPTION') }}</p>
        </div>
        
        <!-- Mobile: Stack buttons vertically -->
        <div class="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-2">
          <!-- Mobile: Primary action first -->
          <woot-button
            size="small"
            class="!bg-grass-500 !text-white w-full xl:w-auto order-1 xl:order-3"
            @click="$emit('add-faq')"
          >
            <lucide-icon name="plus" size="16" class="mr-2" />
            {{ $t('BUSINESS_DATA.FAQS.ADD_NEW') }}
          </woot-button>
          
          <!-- Mobile: Secondary actions in a row -->
          <div class="flex gap-2 order-2 xl:order-1 flex-wrap">
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isExporting"
              :disabled="isExporting"
              @click="onExport"
            >
              <lucide-icon name="download" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.DOWNLOAD') }}</span> XLSX
            </woot-button>
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isImporting"
              :disabled="isImporting"
              @click="triggerImport"
            >
              <lucide-icon name="upload" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.UPLOAD') }}</span> XLSX/CSV
            </woot-button>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="hidden"
            @change="onFileSelected"
          />
        </div>
      </div>

      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BUSINESS_DATA.FAQS.IMPORT_HINT') }}
      </p>
    </div>
    
    <!-- Search and Filters -->
    <div class="flex flex-col xl:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <div class="relative w-full lg:w-auto">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="`🔍 ${$t('BUSINESS_DATA.FAQS.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="onSearch"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-hichat-500 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
      
      <!-- Filter Controls -->
      <div class="flex flex-col gap-3 xl:flex-row xl:flex-wrap">
        <!-- Category Filter -->
        <div class="relative w-full xl:w-auto xl:flex-1">
          <select 
            v-model="selectedCategory" 
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl w-full xl:w-48 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="padding-left: 2.5rem; -webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.FAQS.ALL_CATEGORIES') }}</option>
            <option
              v-for="category in normalizedCategories"
              :key="category.value"
              :value="category.value"
            >
              {{ category.label }}
            </option>
          </select>
        </div>
        
        <!-- Status Filter -->
        <div class="relative w-full xl:w-auto xl:flex-1">
          <fluent-icon icon="circle" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
          <select 
            v-model="selectedStatus" 
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl w-full xl:w-40 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.FAQS.ALL_STATUS') }}</option>
            <option value="true">{{ $t('BUSINESS_DATA.FAQS.META.ACTIVE') }}</option>
            <option value="false">{{ $t('BUSINESS_DATA.FAQS.META.INACTIVE') }}</option>
          </select>
        </div>

        <!-- Sort by created date -->
        <div class="relative w-full xl:w-auto xl:flex-1">
          <select
            v-model="selectedSort"
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl w-full xl:w-44 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="created_at_desc">{{ $t('BUSINESS_DATA.FAQS.SORT.NEWEST') }}</option>
            <option value="created_at_asc">{{ $t('BUSINESS_DATA.FAQS.SORT.OLDEST') }}</option>
          </select>
        </div>
      </div>
    </div>

    <BulkActionsBar
      :selected-count="selectedFaqCount"
      :bulk-action="bulkAction"
      :is-bulk-deleting="isBulkDeleting"
      :is-all-selected-on-page="isAllSelectedOnPage"
      @activate="bulkUpdateStatus(true)"
      @deactivate="bulkUpdateStatus(false)"
      @delete="bulkDeleteSelected"
      @clear-selection="clearSelections"
      @toggle-select-page="toggleSelectAllOnPage"
    />
    
    <!-- Content Area -->
    <div>
      <!-- Loading State -->
      <div v-if="isLoading" class="py-8 flex items-center justify-center">
        <Spinner size="md" color-scheme="primary" />
      </div>
      
      <!-- Empty State -->
      <div v-else-if="faqs.length === 0" class="text-center py-12">
        <div class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-white/30 dark:border-slate-600/30 max-w-md mx-auto">
          <fluent-icon icon="chat-help" size="48" class="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{{ $t('BUSINESS_DATA.FAQS.EMPTY_TITLE') }}</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">{{ $t('BUSINESS_DATA.FAQS.EMPTY_DESCRIPTION') }}</p>
          <woot-button variant="smooth" class="input-base" @click="$emit('add-faq')">
            {{ $t('BUSINESS_DATA.FAQS.ADD_FIRST_FAQ') }}
          </woot-button>
        </div>
      </div>
      
      <!-- FAQs List -->
      <div v-else>
        <div class="space-y-4">
          <div
            v-for="faq in faqs"
            :key="faq.id"
            class="group bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-md p-2 border border-n-gray-1 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-800/80 hover:border-blue-200/50 dark:hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
          >
            <div class="flex items-start gap-2 sm:gap-3">
              <input
                type="checkbox"
                class="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-slate-300 text-hichat-500 focus:ring-hichat-400"
                :checked="isFaqSelected(faq.id)"
                @change="toggleFaqSelection(faq.id, $event.target.checked)"
              />

              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <h4 class="text-base font-semibold text-slate-900 dark:text-slate-100 break-words" v-html="hl(faq.question)"></h4>
                  <div class="flex items-center gap-2 flex-shrink-0">                   
                    <span
                      class="px-2 py-0.5 rounded-lg text-xs font-medium"
                      :class="{
                        'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400': faq.is_active,
                        'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': !faq.is_active
                      }"
                    >
                      {{ faq.is_active ? $t('BUSINESS_DATA.GENERAL.ACTIVE') : $t('BUSINESS_DATA.FAQS.INACTIVE') }}
                    </span>
                  </div>
                </div>

                <template
                  v-for="steps in [Array.isArray(faq.answer_steps) ? faq.answer_steps : []]"
                  :key="`faq-${faq.id}-steps-${steps.length}`"
                >
                  <div class="space-y-1">
                    <div v-if="steps.length" class="space-y-1">
                      <div
                        v-for="(step, index) in steps"
                        :key="`faq-${faq.id}-step-${index}`"
                        class="flex gap-3"
                      >
                        <div class="flex flex-col items-center pt-1 w-7">
                          <span class="flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold bg-hichat-50 text-hichat-700 dark:bg-hichat-500/20 dark:text-hichat-100 border border-hichat-100 dark:border-hichat-500/40">
                            {{ index + 1 }}
                          </span>
                          <span
                            v-if="index < steps.length - 1"
                            class="flex-1 w-px bg-slate-200 dark:bg-slate-700 mt-2"
                          />
                        </div>
                        <div class="flex flex-col justify-center min-w-0 space-y-3 mt-1.5">
                          <p
                            class="mb-0 text-sm leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-line break-words"
                            v-html="hl(step.message)"
                          />

                          <div v-if="step.images && step.images.length" class="flex flex-wrap gap-2">
                            <div
                              v-for="image in step.images"
                              :key="`step-img-${faq.id}-${index}-${image.id}`"
                              class="relative h-12 w-12 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-slate-600 dark:bg-slate-700"
                            >
                              <img
                                :src="image.thumb_url || image.file_url"
                                :alt="image.title || faq.question"
                                class="h-full w-full object-cover"
                              />
                            </div>
                            <span
                              v-if="(step.image_item_ids?.length || 0) > step.images.length"
                              class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                            >
                              {{ step.image_item_ids.length }} {{ $t('BUSINESS_DATA.FAQS.IMAGES_COUNT') }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p
                      v-else
                      class="text-sm leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-line break-words"
                      v-html="hl(getAnswerPreview(faq))"
                    ></p>

                    <template
                      v-for="attachments in [getUnusedAttachments(faq, steps)]"
                      :key="`faq-${faq.id}-attachments-${attachments.length}`"
                    >
                      <div
                        v-if="attachments.length"
                        class="flex flex-wrap gap-2"
                      >
                        <div
                          v-for="image in attachments"
                          :key="`faq-${faq.id}-image-${image.id}`"
                          class="relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-slate-600 dark:bg-slate-700"
                        >
                          <img
                            :src="image.thumb_url || image.file_url"
                            :alt="image.title || faq.question"
                            class="h-full w-full object-cover"
                          />
                        </div>
                      </div>
                    </template>

                    <div class="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                      <div class="flex items-center gap-4">
                        <span class="font-mono">ID: {{ faq.id }}</span>
                        <span>{{ formatDate(faq.created_at) }}</span>
                        <span
                          v-if="faq.business_category"
                          class="px-2 py-1 rounded text-xs"
                          :style="{ backgroundColor: faq.business_category.color + '20', color: faq.business_category.color }"
                        >
                          {{ faq.business_category.name }}
                        </span>
                      </div>

                      <div class="flex items-center gap-1 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                        <woot-button
                          v-tooltip.top="faq.is_active ? $t('GENERAL.TOOLTIPS.HIDE') : $t('GENERAL.TOOLTIPS.SHOW')"
                          variant="smooth"
                          size="small"
                          :icon="faq.is_active ? 'eye-hide' : 'eye-show'"
                          color-scheme="secondary"
                          @click="toggleFaqStatus(faq)"
                        />

                        <woot-button
                          v-tooltip.top="$t('GENERAL.TOOLTIPS.EDIT')"
                          variant="smooth"
                          size="small"
                          color-scheme="secondary"
                          icon="edit"
                          @click="$emit('edit-faq', faq)"
                        />

                        <woot-button
                          v-tooltip.top="$t('GENERAL.TOOLTIPS.DELETE')"
                          variant="smooth"
                          size="small"
                          icon="dismiss-circle"
                          color-scheme="alert"
                          @click="deleteFaq(faq)"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  <!-- Pagination -->
  <TableFooter
    v-if="!isLoading && faqs.length > 0 && pagination.total_pages > 1"
    :current-page="pagination.current_page"
    :total-count="pagination.total_count"
    :page-size="pagination.per_page"
    class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
    @page-change="goToPage"
  />
    <ImportResultsModal
      :show="showImportResultsModal"
      :results="importResults || { created: 0, updated: 0, failed_count: 0, failed: [] }"
      @close="closeImportResults"
    />
</div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { highlightText as highlightTextUtil } from 'shared/utils/searchHighlight';
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper';
import BulkActionsBar from './BulkActionsBar.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import ImportResultsModal from '../ImportResultsModal.vue';

export default {
  name: 'FAQsTab',
  
  components: { 
    Spinner, 
    BulkActionsBar,
    TableFooter,
    ImportResultsModal,
  },
  
  props: {
    faqs: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    },
    isExporting: {
      type: Boolean,
      default: false
    },
    isImporting: {
      type: Boolean,
      default: false
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    isBulkDeleting: {
      type: Boolean,
      default: false
    },
    bulkAction: {
      type: String,
      default: ''
    },
    importResults: {
      type: Object,
      default: null
    }
  },
  
  emits: [
    'add-faq',
    'edit-faq',
    'search',
    'filter-change',
    'page-change',
    'toggle-select',
    'toggle-select-page',
    'clear-selection',
    'bulk-update-status',
    'bulk-delete',
    'toggle-status',
    'delete-faq',
    'export',
    'import'
  ],
  
  data() {
    return {
      searchQuery: '',
      selectedCategory: '',
      selectedStatus: '',
      selectedSort: 'created_at_desc',
      searchTimeout: null,
      showImportResultsModal: false,
    }
  },
  
  watch: {
    importResults: {
      handler(newVal) {
        if (newVal && (newVal.failed_count > 0 || newVal.created > 0 || newVal.updated > 0)) {
          // Auto show modal if there are results
        }
      },
      immediate: true
    }
  },
  
  computed: {
    normalizedCategories() {
      const map = new Map()
      ;(this.categories || []).forEach(category => {
        const normalized = this.normalizeCategory(category)
        if (normalized && normalized.value && !map.has(normalized.value)) {
          map.set(normalized.value, normalized.label)
        }
      })
      return Array.from(map.entries()).map(([value, label]) => ({ value: String(value), label }))
    },


    selectedFaqCount() {
      return this.selectedIds.length
    },

    hasBulkSelection() {
      return this.selectedFaqCount > 0
    },

    isAllSelectedOnPage() {
      if (!this.faqs || this.faqs.length === 0) {
        return false
      }
      const pageIds = this.faqs.map(faq => faq.id)
      return pageIds.every(id => this.selectedIds.includes(id))
    }
  },

  watch: {
    categories: {
      immediate: true,
      handler() {
        if (!this.selectedCategory) {
          return
        }
        const exists = this.normalizedCategories.some(category => category.value === this.selectedCategory)
        if (!exists) {
          this.selectedCategory = ''
          this.onFilterChange()
        }
      }
    },
    importResults: {
      handler(newVal) {
        if (newVal && (newVal.created > 0 || newVal.updated > 0 || newVal.failed_count > 0)) {
          // Import results available
        }
      },
      immediate: true
    }
  },

  methods: {
    onExport() {
      this.$emit('export')
    },

    triggerImport() {
      this.$refs.fileInput?.click()
    },

    onFileSelected(e) {
      const file = e.target.files?.[0]
      if (!file) return
      this.$emit('import', file)
      e.target.value = ''

      if (this.isMobileDevice()) {
        this.searchQuery = this.searchQuery ? `${this.searchQuery} ` : ''
        this.searchQuery = this.searchQuery.trim()
      }
    },
    // Shared highlight wrapper
    hl(text) {
      return highlightTextUtil(text, this.searchQuery, 'search-highlight')
    },
    normalizeCategory(category) {
      if (category === null || category === undefined || category === '') {
        return {
          value: 'uncategorized',
          label: this.$t('BUSINESS_DATA.FAQS.UNCATEGORIZED')
        }
      }
      if (typeof category === 'string') {
        const trimmed = category.trim()
        if (!trimmed) {
          return {
            value: 'uncategorized',
            label: this.$t('BUSINESS_DATA.FAQS.UNCATEGORIZED')
          }
        }
        return { value: trimmed, label: trimmed }
      }

      const value = category.id ?? category.slug ?? category.code ?? category.name ?? ''
      const label = category.name ?? category.display_name ?? category.label ?? category.slug ?? category.code

      if (!value && !label) {
        return {
          value: 'uncategorized',
          label: this.$t('BUSINESS_DATA.FAQS.UNCATEGORIZED')
        }
      }

      return {
        value: String(label || value),  // Use name as value, not ID
        label: label || this.$t('BUSINESS_DATA.FAQS.UNCATEGORIZED')
      }
    },

    getUnusedAttachments(faq, steps = []) {
      const usedIds = new Set()
      steps.forEach(step => {
        (step.image_item_ids || []).forEach(id => usedIds.add(Number(id)))
      })
      return (faq?.attached_images || []).filter(image => !usedIds.has(Number(image.id)))
    },

    getAnswerPreview(faq) {
      return faq?.short_answer || faq?.answer || ''
    },

    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      
      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery)
      }, 500)
    },
    
    onFilterChange() {
      this.$emit('filter-change', {
        category: this.selectedCategory,
        status: this.selectedStatus,
        sort: this.selectedSort,
      })
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.current_page) {
        this.$emit('page-change', page)
      }
    },

    isFaqSelected(faqId) {
      return this.selectedIds.includes(faqId)
    },

    toggleFaqSelection(faqId, checked) {
      this.$emit('toggle-select', { id: faqId, checked })
    },

    toggleSelectAllOnPage() {
      const pageIds = this.faqs.map(faq => faq.id)
      this.$emit('toggle-select-page', { ids: pageIds, selectAll: !this.isAllSelectedOnPage })
    },

    clearSelections() {
      this.$emit('clear-selection')
    },

    bulkDeleteSelected() {
      this.$emit('bulk-delete')
    },

    bulkUpdateStatus(isActive) {
      this.$emit('bulk-update-status', isActive)
    },

    toggleFaqStatus(faq) {
      this.$emit('toggle-status', faq)
    },

    deleteFaq(faq) {
      this.$emit('delete-faq', faq)
    },
    
    formatDate(dateString) {
      return formatDateTimeLocalized(dateString, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    },

    getFaqCategory(faq) {
      if (!faq) return ''
      return faq.business_category?.name || faq.category || ''
    },
    
    getTextColor(backgroundColor) {
      if (!backgroundColor) return '#000000'
      const hex = String(backgroundColor).replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5 ? '#000000' : '#FFFFFF'
    },

    showImportResults() {
      this.showImportResultsModal = true;
    },
    
    closeImportResults() {
      this.showImportResultsModal = false;
    },
  }
}
</script>

<style scoped>
.search-highlight {
  background-color: #fef08a;
  color: #92400e;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.dark .search-highlight {
  background-color: #fbbf24;
  color: #451a03;
}
</style>
